import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { BookOpen, ChevronDown, ChevronUp, Edit2, Loader2, RefreshCw, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

// Full 7-dimension Brand DNA editor — extracted VERBATIM from Settings.js so the
// new BrandDnaView and the Settings page share ONE implementation (no duplicate).
// Behaviour + save path unchanged: PUT /settings/brand-dna/structured and
// POST /settings/brand-dna/quick-adjust. Self-contained (own state + own load),
// single prop { getAuthHeader }. Already dark-styled (app theme).

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;


const DNA_DIMENSIONS = [
  {
    key: 'brand_identity', label: 'Brand Identity', number: '01', color: '#00D9C8',
    summary: (d) => [d?.positioning, d?.usp].filter(Boolean).join(' · ') || '—',
    fields: [
      { key: 'positioning', label: 'Positioning', multiline: true },
      { key: 'product_promise', label: 'Product Promise', multiline: true },
      { key: 'usp', label: 'Alleinstellungsmerkmal', multiline: false },
      { key: 'market_position', label: 'Marktposition', multiline: true },
    ],
  },
  {
    key: 'voice_and_tone', label: 'Voice & Ton', number: '02', color: '#A78BFA',
    summary: (d, t) => [(d?.personality_traits || []).join(', '), d?.address_form ? `${t?.settings?.dnaExtras?.salutationLabel || 'Ansprache'}: ${d.address_form}` : ''].filter(Boolean).join(' · ') || '—',
    fields: [
      { key: 'address_form', label: 'Ansprache', multiline: false },
      { key: 'formality_level', label: 'Formalitätslevel', multiline: false },
    ],
    listFields: [{ key: 'personality_traits', label: 'Persönlichkeits-Adjektive' }],
    copyFields: true,
  },
  {
    key: 'copy_rules', label: 'Schreibregeln', number: '03', color: '#34D399',
    summary: (d) => { const dos = (d?.dos||[]).length; const donts = (d?.donts||[]).length; return dos||donts ? `${dos} Do's · ${donts} Don'ts` : '—'; },
    listFields: [
      { key: 'dos', label: "Do's" }, { key: 'donts', label: "Don'ts" },
      { key: 'forbidden_phrases', label: 'Verbotene Phrasen' }, { key: 'mandatory_elements', label: 'Pflicht-Elemente' },
    ],
  },
  {
    key: 'copy_metrics', label: 'Textlängen', number: '04', color: '#F59E0B',
    summary: (d, t) => {
      if (!d?.posting_frequency) return '—';
      const words = t?.settings?.dnaSummaries?.words || 'Wörter';
      return `Headlines ${d.headline_words_min}–${d.headline_words_max} ${words} · ${d.posting_frequency}`;
    },
    fields: [
      { key: 'headline_words_min', label: 'Headline Min.', multiline: false }, { key: 'headline_words_max', label: 'Headline Max.', multiline: false },
      { key: 'body_words_min', label: 'Body Min.', multiline: false }, { key: 'body_words_max', label: 'Body Max.', multiline: false },
      { key: 'hashtags_min', label: 'Hashtags Min.', multiline: false }, { key: 'hashtags_max', label: 'Hashtags Max.', multiline: false },
      { key: 'posting_frequency', label: 'Posting-Rhythmus', multiline: false },
    ],
  },
  {
    key: 'core_vocabulary', label: 'Markenwörter', number: '05', color: '#60A5FA',
    summary: (d, t) => {
      const m = (d?.must_use||[]).length; const f = (d?.forbidden||[]).length;
      if (!m && !f) return '—';
      const mustLabel = t?.settings?.dnaSummaries?.mustUse || 'Pflicht';
      const forbiddenLabel = t?.settings?.dnaSummaries?.forbidden || 'verboten';
      return `${m} ${mustLabel} · ${f} ${forbiddenLabel}`;
    },
    listFields: [{ key: 'must_use', label: 'Pflicht-Wörter' }, { key: 'forbidden', label: 'Verbotene Wörter' }, { key: 'brand_terms', label: 'Marken-Eigenbegriffe' }],
  },
  {
    key: 'visual_identity', label: 'Visueller Stil', number: '06', color: '#F472B6',
    summary: (d) => [(d?.colors||[]).map(c=>c.hex).join(', '), d?.headline_font].filter(Boolean).join(' · ') || '—',
    fields: [{ key: 'headline_font', label: 'Überschriften-Font', multiline: false }, { key: 'body_font', label: 'Fließtext-Font', multiline: false }],
    listFields: [{ key: 'photo_styles', label: 'Foto-Stile' }, { key: 'design_elements', label: 'Design-Elemente' }],
    colorField: true,
  },
  {
    key: 'content_strategy', label: 'Content-Strategie & CTAs', number: '07', color: '#FB923C',
    summary: (d, t) => {
      const th = (d?.themes||[]).length; const c = (d?.cta_library||[]).length;
      if (!th && !c) return '—';
      const themesLabel = t?.settings?.dnaSummaries?.themes || 'Themen';
      return `${th} ${themesLabel} · ${c} CTAs`;
    },
    fields: [{ key: 'value_proposition', label: 'Value Proposition', multiline: true }],
  },
];

// ── DNADimensionRow ───────────────────────────────────────────────────────────

const DNADimensionRow = ({ config, data, onChange, t }) => {
  const [open, setOpen] = useState(false);
  const [editingField, setEditField] = useState(null);
  const [draft, setDraft] = useState('');

  const isDefined = data && (
    (config.fields && config.fields.some(f => data[f.key])) ||
    (config.listFields && config.listFields.some(f => (data[f.key]||[]).length > 0))
  );

  const startEdit = (key, val) => { setDraft(Array.isArray(val) ? val.join('\n') : String(val??'')); setEditField(key); };
  const saveEdit = (key) => {
    const isList = config.listFields?.some(f => f.key === key);
    onChange({ ...data, [key]: isList ? draft.split('\n').map(s=>s.trim()).filter(Boolean) : draft });
    setEditField(null);
  };

  const labelFor = (field) => (t?.settings?.dnaFieldLabels?.[field.key]) || field.label;
  const dimensionLabel = (t?.settings?.dnaDimensions?.[config.key]) || config.label;
  const notDefined = t?.settings?.dnaExtras?.notDefined || 'Nicht definiert';
  const oneEntryPerLine = t?.settings?.dnaExtras?.oneEntryPerLine || 'Ein Eintrag pro Zeile';

  return (
    <Card className="bg-[#0A1628] border-transparent">
      <CardContent className="p-0">
        <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => setOpen(o => !o)}>
          <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: `${config.color}20`, color: config.color }}>{config.number}</span>
          <span className="text-white font-medium text-sm flex-1">{dimensionLabel}</span>
          <span className={`text-xs mr-2 ${isDefined ? 'text-[#34D399]' : 'text-[#5A6C7D]'}`}>{isDefined ? '✓' : '○'}</span>
          {open ? <ChevronUp className="w-4 h-4 text-[#5A6C7D]" /> : <ChevronDown className="w-4 h-4 text-[#5A6C7D]" />}
        </button>
        {!open && <p className="px-4 pb-3 text-xs text-[#5A6C7D]">{config.summary(data, t)}</p>}
        {open && (
          <div className="px-4 pb-4 space-y-3 border-t border-[#1A2B42]">
            {config.fields?.map(field => (
              <div key={field.key}>
                <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-1">{labelFor(field)}</p>
                {editingField === field.key ? (
                  <div className="space-y-2">
                    {field.multiline
                      ? <Textarea value={draft} onChange={e=>setDraft(e.target.value)} className="min-h-[80px] bg-[#1A2B42] border-[#00D9C8]/30 text-white text-sm resize-none" autoFocus />
                      : <Input value={draft} onChange={e=>setDraft(e.target.value)} className="bg-[#1A2B42] border-[#00D9C8]/30 text-white text-sm" autoFocus />
                    }
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditField(null)} className="text-xs text-[#5A6C7D] hover:text-white px-3 py-1">{t?.common?.cancel || 'Abbrechen'}</button>
                      <button onClick={() => saveEdit(field.key)} className="text-xs px-3 py-1 rounded-lg" style={{ background: '#00D9C820', color: '#00D9C8' }}>{t?.common?.save || 'Speichern'}</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <p className="text-[#A0AEC0] text-sm flex-1">{data?.[field.key] ? String(data[field.key]) : <span className="text-[#5A6C7D] italic">{notDefined}</span>}</p>
                    <button onClick={() => startEdit(field.key, data?.[field.key])} className="text-[#5A6C7D] hover:text-white flex-shrink-0"><Edit2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
            {config.listFields?.map(field => {
              const items = data?.[field.key] || [];
              return (
                <div key={field.key}>
                  <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-1">{labelFor(field)}</p>
                  {editingField === field.key ? (
                    <div className="space-y-2">
                      <Textarea value={draft} onChange={e=>setDraft(e.target.value)} className="min-h-[80px] bg-[#1A2B42] border-[#00D9C8]/30 text-white text-sm resize-none" placeholder={oneEntryPerLine} autoFocus />
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setEditField(null)} className="text-xs text-[#5A6C7D] hover:text-white px-3 py-1">{t?.common?.cancel || 'Abbrechen'}</button>
                        <button onClick={() => saveEdit(field.key)} className="text-xs px-3 py-1 rounded-lg" style={{ background: '#00D9C820', color: '#00D9C8' }}>{t?.common?.save || 'Speichern'}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <div className="flex-1 flex flex-wrap gap-1.5">
                        {items.length > 0 ? items.map((item,i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${config.color}15`, color: config.color }}>{String(item)}</span>
                        )) : <span className="text-[#5A6C7D] text-sm italic">{notDefined}</span>}
                      </div>
                      <button onClick={() => startEdit(field.key, items)} className="text-[#5A6C7D] hover:text-white flex-shrink-0"><Edit2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              );
            })}
            {config.colorField && (data?.colors||[]).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-2">{t?.settings?.dnaExtras?.colorPalette || 'Farbpalette'}</p>
                <div className="flex gap-2 flex-wrap">
                  {data.colors.map((c,i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded" style={{ background: c.hex, border: '1px solid rgba(255,255,255,0.1)' }} />
                      <span className="text-xs text-[#A0AEC0]">{c.hex}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {config.copyFields && (
              <>
                {(data?.reference_copy_positive||[]).length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#34D399] mb-2">{t?.settings?.dnaExtras?.referenceGood || 'So soll es klingen'}</p>
                    {data.reference_copy_positive.map((ex,i) => (
                      <div key={i} className="bg-[#1A2B42] rounded-lg p-3 mb-2">
                        <p className="text-white text-sm font-medium">"{ex.text}"</p>
                        {ex.reason && <p className="text-[#5A6C7D] text-xs mt-1">{ex.reason}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {(data?.reference_copy_negative||[]).length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-red-400 mb-2">{t?.settings?.dnaExtras?.referenceBad || 'So NICHT'}</p>
                    {data.reference_copy_negative.map((ex,i) => (
                      <div key={i} className="bg-[#1A2B42] rounded-lg p-3 mb-2 border border-red-900/30">
                        <p className="text-red-300 text-sm font-medium">"{ex.text}"</p>
                        {ex.reason && <p className="text-[#5A6C7D] text-xs mt-1">{ex.reason}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── BrandDNAUebersicht (neu — 7 Dimensionen) ─────────────────────────────────

const BrandDNAUebersicht = ({ getAuthHeader }) => {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [brandDNA, setBrandDNA] = useState(null);          // strukturiertes Objekt (neu)
  const [brandManual, setBrandManual] = useState('');       // Legacy-Freitext
  const [completeness, setCompleteness] = useState(0);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadJobId, setUploadJobId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [manualFilename, setManualFilename] = useState('');
  const [showLegacy, setShowLegacy] = useState(false);
  // legacy fields kept for upload polling
  const [platformsAnalyzed, setPlatformsAnalyzed] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [regenJobId, setRegenJobId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() })
      .then(r => {
        // Strukturiertes Objekt (neu) hat Vorrang
        const structured = r.data.brandDNA || r.data.structured_dna;
        if (structured?.brand_identity) {
          setBrandDNA(structured);
          setCompleteness(structured.completeness_score || 0);
        }
        // Legacy-Freitext immer laden (für Upload/Edit)
        setBrandManual(r.data.brandManual || '');
        setPlatformsAnalyzed(r.data.platformsAnalyzed || []);
        setEditText(r.data.brandManual || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll for upload job status
  useEffect(() => {
    if (!uploadJobId) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/settings/brand-dna/upload-manual/${uploadJobId}`, { headers: getAuthHeader() });
        setUploadStatus(r.data);
        if (r.data.status === 'completed') {
          clearInterval(interval);
          setUploading(false);
          setUploadJobId(null);
          toast.success('Brand Manual analysiert und gespeichert!');
          // Reload brand manual
          const dna = await axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() });
          setBrandManual(dna.data.brandManual || '');
          setEditText(dna.data.brandManual || '');
        } else if (r.data.status === 'error') {
          clearInterval(interval);
          setUploading(false);
          setUploadJobId(null);
          toast.error(r.data.error || 'Verarbeitung fehlgeschlagen');
        }
      } catch (err) {
        clearInterval(interval);
        setUploading(false);
        setUploadJobId(null);
        toast.error(err?.response?.data?.detail || 'Status-Abfrage fehlgeschlagen. Bitte erneut versuchen.');
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [uploadJobId]);

  // Poll for regenerate job status
  useEffect(() => {
    if (!regenJobId) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/settings/brand-dna/upload-manual/${regenJobId}`, { headers: getAuthHeader() });
        if (r.data.status === 'completed') {
          clearInterval(interval);
          setRegenerating(false);
          setRegenJobId(null);
          toast.success('Brand Manual neu generiert!');
          const dna = await axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() });
          setBrandManual(dna.data.brandManual || '');
          setEditText(dna.data.brandManual || '');
        } else if (r.data.status === 'error') {
          clearInterval(interval);
          setRegenerating(false);
          setRegenJobId(null);
          toast.error(r.data.error || 'Generierung fehlgeschlagen');
        }
      } catch (err) {
        clearInterval(interval);
        setRegenerating(false);
        setRegenJobId(null);
        toast.error(err?.response?.data?.detail || 'Status-Abfrage fehlgeschlagen. Bitte erneut versuchen.');
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [regenJobId]);

  const handleRegenerateManual = async () => {
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/settings/brand-dna/regenerate-manual`, {}, { headers: getAuthHeader(), timeout: 15000 });
      setRegenJobId(r.data.job_id);
    } catch (err) {
      setRegenerating(false);
      toast.error(err.response?.data?.detail || 'Fehler beim Generieren');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/brand-dna/brand-manual`, editText,
        { headers: { ...getAuthHeader(), 'Content-Type': 'text/plain' } });
      setBrandManual(editText);
      setEditing(false);
      toast.success('Brand Manual gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const handleUploadManual = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) {
      toast.error('Nur PDF- und DOCX-Dateien sind erlaubt.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    setUploadStatus({ status: 'uploading' });
    setManualFilename(file.name);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/settings/brand-dna/upload-manual`, formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
      setUploadJobId(r.data.job_id);
      setUploadStatus({ status: 'processing' });
    } catch (err) {
      setUploading(false);
      setUploadStatus(null);
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    }
    e.target.value = '';
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" /></div>;

  return (
    <div className="space-y-4">
      {/* Quick Anpassung */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium">{lang === 'de' ? 'Anpassung vorschlagen' : 'Suggest adjustment'}</p>
              <p className="text-[#A0AEC0] text-xs mt-0.5">{lang === 'de' ? 'Fällt dir laufend etwas an deinem Content auf, das du verändern möchtest?' : 'Notice something recurring in your content you\'d like to change?'}</p>
            </div>
            {brandDNA && (
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 bg-[#0A1628] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${completeness}%`, background: completeness >= 80 ? '#34D399' : completeness >= 50 ? '#F59E0B' : '#EF4444' }} />
                </div>
                <span className="text-xs text-[#A0AEC0]">{completeness}%</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              id="dna-quick-input"
              placeholder={lang === 'de' ? 'z.B. "Weniger Hashtags" oder "Tonalität lockerer"...' : 'e.g. "Fewer hashtags" or "More casual tone"...'}
              className="bg-[#0A1628] border-transparent text-white text-sm min-h-[60px] resize-none flex-1"
            />
            <Button
              onClick={async () => {
                const val = document.getElementById('dna-quick-input')?.value?.trim();
                if (!val) return;
                setSaving(true);
                try {
                  const res = await axios.post(
                    `${API}/settings/brand-dna/quick-adjust`,
                    { adjustment: val, language: lang },
                    { headers: getAuthHeader(), timeout: 60000 }
                  );
                  if (res.data.brand_dna) {
                    setBrandDNA(res.data.brand_dna);
                    setCompleteness(res.data.brand_dna.completeness_score || completeness);
                  }
                  document.getElementById('dna-quick-input').value = '';
                  toast.success(lang === 'de' ? 'Anpassung gespeichert' : 'Adjustment saved');
                } catch (err) {
                  toast.error(err.response?.data?.detail || (lang === 'de' ? 'Fehler beim Anpassen' : 'Adjustment failed'));
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-medium px-4 self-end"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === 'de' ? 'Anwenden' : 'Apply')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(lang === 'de'
              ? ['Weniger Hashtags', 'Lockerer im Ton', 'Mehr lokale Referenzen', 'Kürzere Headlines']
              : ['Fewer hashtags', 'More casual tone', 'More local references', 'Shorter headlines']
            ).map(hint => (
              <button
                key={hint}
                onClick={() => { const el = document.getElementById('dna-quick-input'); if (el) el.value = hint; }}
                className="text-xs px-3 py-1.5 rounded-full border border-[#2A3B52] bg-[#0A1628]/60 text-[#A0AEC0] hover:text-white hover:border-[#00D9C8] hover:bg-[#0A1628] transition-colors"
              >
                {hint}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 7 Dimensionen */}
      {brandDNA ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[#A0AEC0] text-xs font-medium uppercase tracking-wider">{lang === 'de' ? '7 Dimensionen' : '7 Dimensions'}</p>
            <Button variant="ghost" size="sm" onClick={async () => {
              setRegenerating(true);
              try {
                await axios.post(`${API}/settings/generate-brand-dna`, { inspiration_posts: [] }, { headers: getAuthHeader(), timeout: 120000 });
                const r = await axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() });
                const s = r.data.brandDNA || r.data.structured_dna;
                if (s?.brand_identity) { setBrandDNA(s); setCompleteness(s.completeness_score||0); }
                toast.success(t.settings.brandDnaRegenerated);
              } catch { toast.error(t.settings.brandDnaRegenerateFailed); } finally { setRegenerating(false); }
            }} disabled={regenerating} className="text-[#A0AEC0] hover:text-[#00D9C8] text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regenerating ? 'animate-spin' : ''}`} />{t.settings.regenerate}
            </Button>
          </div>
          {DNA_DIMENSIONS.map(config => (
            <DNADimensionRow
              key={config.key}
              config={config}
              data={brandDNA[config.key]}
              t={t}
              onChange={async (newData) => {
                const updated = { ...brandDNA, [config.key]: newData };
                setBrandDNA(updated);
                try {
                  await axios.put(`${API}/settings/brand-dna/structured`, { brand_dna: updated }, { headers: getAuthHeader() });
                  toast.success('Gespeichert');
                } catch { toast.error('Fehler beim Speichern'); }
              }}
            />
          ))}
        </div>
      ) : (
        <div>
          {/* Kein strukturiertes Objekt → Legacy-Ansicht */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#A0AEC0] text-xs">Keine strukturierte Brand DNA gefunden.</p>
            <Button size="sm" onClick={async () => {
              setRegenerating(true);
              try {
                await axios.post(`${API}/settings/generate-brand-dna`, { inspiration_posts: [] }, { headers: getAuthHeader(), timeout: 120000 });
                const r = await axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() });
                const s = r.data.brandDNA || r.data.structured_dna;
                if (s?.brand_identity) { setBrandDNA(s); setCompleteness(s.completeness_score||0); }
                toast.success('Brand DNA generiert');
              } catch { toast.error('Fehler'); } finally { setRegenerating(false); }
            }} disabled={regenerating} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-medium">
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Brand DNA generieren
            </Button>
          </div>
        </div>
      )}

      {/* Brand-Manual-Upload: siehe BrandManualImport oben auf der Brand-DNA-Seite (alter Upload entfernt —
          er schrieb nur Legacy-Felder, die der Content nicht liest). */}

      {/* Legacy Brand Manual — aufklappbar */}
      {brandManual && (
        <Card className="bg-[#1A2B42] border-transparent">
          <button onClick={() => setShowLegacy(s => !s)} className="w-full flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              <BookOpen className="w-4 h-4 text-[#5A6C7D]" />
              <span className="text-[#A0AEC0] text-sm">{t.settings.dnaExtras.showLegacyManual}</span>
              {platformsAnalyzed.length > 0 && <span className="text-[10px] text-[#00D9C8]">{platformsAnalyzed.join(', ')}</span>}
            </div>
            {showLegacy ? <ChevronUp className="w-4 h-4 text-[#5A6C7D]" /> : <ChevronDown className="w-4 h-4 text-[#5A6C7D]" />}
          </button>
          {showLegacy && (
            <CardContent className="border-t border-[#0A1628]/50">
              {editing ? (
                <div className="space-y-3 pt-3">
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                    className="bg-[#0A1628] border-transparent text-white text-sm min-h-[300px] font-mono leading-relaxed" />
                  <div className="flex gap-2">
                    <Button onClick={async () => {
                      setSaving(true);
                      try {
                        await axios.put(`${API}/settings/brand-dna/brand-manual`, editText, { headers: { ...getAuthHeader(), 'Content-Type': 'text/plain' } });
                        setBrandManual(editText); setEditing(false); toast.success('Brand Manual gespeichert');
                      } catch { toast.error('Fehler'); } finally { setSaving(false); }
                    }} disabled={saving} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold flex-1">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Speichern
                    </Button>
                    <Button onClick={() => { setEditText(brandManual); setEditing(false); }} variant="outline" className="border-[#1A2B42] text-[#A0AEC0]">Abbrechen</Button>
                  </div>
                </div>
              ) : (
                <div className="pt-3">
                  <pre className="text-[#A0AEC0] text-xs whitespace-pre-wrap leading-relaxed font-sans mb-3">{brandManual}</pre>
                  <Button variant="outline" size="sm" onClick={() => { setEditing(true); setEditText(brandManual); }} className="border-[#00D9C8]/30 text-[#00D9C8]">
                    <Edit2 className="w-3 h-3 mr-1" />Bearbeiten
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};


export default BrandDNAUebersicht;
